import {
    LogEntry,
    LoggerConfig,
    LogLevel,
    LogMeta,
} from './types';

export class Logger {
  private static instance: Logger;
  private config: LoggerConfig;
  private messageCounters: Map<LogLevel, number> = new Map();
  private correlationId: string | null = null;
  private sessionId: string;

  private constructor(config: LoggerConfig) {
    this.config = config;
    this.sessionId = this.generateSessionId();
    this.initializeCounters();
  }

  public static getInstance(config?: LoggerConfig): Logger {
    if (!Logger.instance) {
      if (!config) {
        throw new Error('Logger must be initialized with config on first call');
      }
      Logger.instance = new Logger(config);
    }
    return Logger.instance;
  }

  private initializeCounters(): void {
    this.messageCounters.set('debug', 0);
    this.messageCounters.set('info', 0);
    this.messageCounters.set('warn', 0);
    this.messageCounters.set('error', 0);
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  public setCorrelationId(id: string | null): void {
    this.correlationId = id;
  }

  public getCorrelationId(): string | null {
    return this.correlationId;
  }

  private shouldLog(level: LogLevel, isError: boolean = false): boolean {
    // Always log errors
    if (isError || level === 'error') {
      return true;
    }

    // Check if level meets minimum threshold
    if (!this.meetsMinimumLevel(level)) {
      return false;
    }

    // Apply sampling based on environment
    const counter = this.messageCounters.get(level) || 0;
    this.messageCounters.set(level, counter + 1);

    // Sampling logic based on config
    return (counter + 1) % this.config.sampling === 1;
  }

  private meetsMinimumLevel(level: LogLevel): boolean {
    const levels = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(level);
    const minLevelIndex = levels.indexOf(this.config.level);
    return currentLevelIndex >= minLevelIndex;
  }

  private async logInternal(
    level: LogLevel,
    message: string,
    meta?: LogMeta | Error,
    isError: boolean = false
  ): Promise<void> {
    if (!this.shouldLog(level, isError)) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      meta: this.sanitizeMeta(meta),
      correlationId: this.correlationId || undefined,
      module: this.config.module,
      sessionId: this.sessionId,
    };

    // Send to all transports
    const promises = this.config.transports.map(transport =>
      transport.log(entry).catch(error => {
        // Never throw from logging - just log to console as fallback
        console.error('Logger transport failed:', error);
      })
    );

    await Promise.allSettled(promises);
  }

  private sanitizeMeta(meta?: LogMeta | Error): LogMeta {
    if (!meta) return {};

    if (meta instanceof Error) {
      return {
        error: meta.message,
        stack: meta.stack,
        name: meta.name,
      };
    }

    // Sanitize sensitive fields
    const sanitized = { ...meta };
    const sensitiveKeys = [
      'password',
      'token',
      'authorization',
      'secret',
      'key',
    ];

    for (const key in sanitized) {
      if (
        sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))
      ) {
        sanitized[key] = '[REDACTED]';
      }
    }

    return sanitized;
  }

  public debug(message: string, meta?: LogMeta): void {
    this.logInternal('debug', message, meta);
  }

  public info(message: string, meta?: LogMeta): void {
    this.logInternal('info', message, meta);
  }

  public warn(message: string, meta?: LogMeta): void {
    this.logInternal('warn', message, meta);
  }

  public error(message: string, meta?: LogMeta | Error): void {
    this.logInternal('error', message, meta, true);
  }

  public async flush(): Promise<void> {
    const promises = this.config.transports
      .filter(transport => transport.flush)
      .map(transport => transport.flush!());

    await Promise.allSettled(promises);
  }

  public updateConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
